import React, { useEffect, useState } from 'react';
import { IonPage, IonContent, IonIcon, IonSpinner } from '@ionic/react';
import { useParams, useHistory } from 'react-router-dom';
import { exitOutline, refreshOutline, gridOutline } from 'ionicons/icons';
import DashboardDisplay from '../components/DashboardDisplay';
import { getKioskSession, clearKioskSession, type KioskSession } from '../services/quickAccess';
import { KioskProvider } from '../contexts/KioskContext';
import './KioskDashboardPage.css';

const KioskDashboardPage: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const history = useHistory();
  const [session, setSession] = useState<KioskSession | null>(null);
  const [showControls, setShowControls] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verify kiosk session
    const kioskSession = getKioskSession();

    if (!kioskSession || kioskSession.dashboardId !== dashboardId) {
      // No valid session, redirect to access page
      history.replace('/access');
      return;
    }

    setSession(kioskSession);
    setLoading(false);
  }, [dashboardId, history]);

  // Handle exit kiosk mode
  const handleExit = () => {
    clearKioskSession();
    history.replace('/access');
  };

  // Handle refresh
  const handleRefresh = () => {
    window.location.reload();
  };

  // Toggle controls visibility (tap corner to show)
  const handleCornerTap = () => {
    setShowControls(prev => !prev);
    // Auto-hide after 5 seconds
    if (!showControls) {
      setTimeout(() => setShowControls(false), 5000);
    }
  };

  if (loading) {
    return (
      <IonPage className="kiosk-dashboard-page">
        <IonContent className="kiosk-loading">
          <div className="kiosk-loading-content">
            <IonSpinner name="crescent" />
            <p>Loading dashboard...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!session) {
    return null; // Will redirect
  }

  return (
    <KioskProvider>
      <IonPage className="kiosk-dashboard-page">
        {/* Hidden corner tap zone to show controls */}
        <div
          className="kiosk-corner-tap-zone"
          onClick={handleCornerTap}
          aria-label="Show kiosk controls"
        />

        {/* Control overlay */}
        {showControls && (
          <div className="kiosk-controls-overlay">
            <div className="kiosk-controls-panel">
              <div className="kiosk-controls-header">
                <IonIcon icon={gridOutline} />
                <div className="kiosk-controls-info">
                  <h3>{session.dashboardName}</h3>
                  <p>{session.codeName}</p>
                </div>
              </div>

              <div className="kiosk-controls-actions">
                <button
                  className="kiosk-control-button"
                  onClick={handleRefresh}
                >
                  <IonIcon icon={refreshOutline} />
                  <span>Refresh</span>
                </button>

                <button
                  className="kiosk-control-button danger"
                  onClick={handleExit}
                >
                  <IonIcon icon={exitOutline} />
                  <span>Exit Kiosk</span>
                </button>
              </div>
            </div>

            {/* Backdrop to close */}
            <div
              className="kiosk-controls-backdrop"
              onClick={() => setShowControls(false)}
            />
          </div>
        )}

        {/* Dashboard content */}
        <DashboardDisplay
          dashboardId={dashboardId}
          householdId={session.householdId}
          editMode={false}
        />
      </IonPage>
    </KioskProvider>
  );
};

export default KioskDashboardPage;
